import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { MultiSelect } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import companyImpersonationService from '../../services/companyImpersonationService';
import companyCreditService from '../../services/companyCreditService';
import leadSharingSettingsService from '../../services/leadSharingSettingsService';
import EmployeeRegisterByAdminDialog from './EmployeeRegisterByAdminDialog';
import axiosInstance from '../../config/axios.config';

const CompanyUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [impersonating, setImpersonating] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({ visible: false, user: null });
    const [companyInfo, setCompanyInfo] = useState(null);
    const [showRegisterDialog, setShowRegisterDialog] = useState(false);

    // Pagination
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search (debounced)
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Sort
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);

    // Edit dialog
    const [editDialog, setEditDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        first_name: '',
        last_name: '',
        mobile_number: '',
        handled_lenders: [],
    });
    const [editing, setEditing] = useState(false);

    // Lender options for the "Lenders Handled" multi-select (id → name).
    // /lenders/dropdown returns { success, data: [{ value, label, code, type }] }.
    const [lenderOptions, setLenderOptions] = useState([]);
    useEffect(() => {
        let active = true;
        axiosInstance.get('/company/lenders-dropdown')
            .then((res) => {
                if (!active) return;
                const list = res?.data?.data || [];
                setLenderOptions(list.map((l) => ({ label: l.label, value: l.value })));
            })
            .catch((e) => console.error('Failed to load lenders:', e?.message || 'Unknown error'));
        return () => { active = false; };
    }, []);
    const [formErrors, setFormErrors] = useState({});

    // Toggle states
    const [updatingSharing, setUpdatingSharing] = useState({});
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [statusConfirmDialog, setStatusConfirmDialog] = useState({ visible: false, user: null, newStatus: null });
    const [confirmingStatusChange, setConfirmingStatusChange] = useState(false);

    // Refresh nonce
    const [refreshNonce, setRefreshNonce] = useState(0);

    const toast = useRef(null);

    // Debounce search (P13)
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setFirst(0);
        }, 500);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Access gate + doc title
    useEffect(() => {
        document.title = 'Company Users - interestng';
        if (!companyImpersonationService.isCompanyOwner()) {
            toast.current?.show({
                severity: 'error',
                summary: 'Access Denied',
                detail: 'Only company admins can access this page',
                life: 5000,
            });
            setTimeout(() => {
                window.location.href = '/app/dashboard';
            }, 2000);
        }
    }, []);

    // Load users — single effect with primitive deps (P14)
    useEffect(() => {
        if (!companyImpersonationService.isCompanyOwner()) return;
        let cancelled = false;
        const loadCompanyUsers = async () => {
            setLoading(true);
            try {
                const page = Math.floor(first / rows) + 1;
                const result = await companyImpersonationService.getImpersonatableUsers({
                    page,
                    per_page: rows,
                    search: debouncedSearch,
                    sort_field: sortField,
                    sort_order: sortOrder === 1 ? 'asc' : 'desc',
                });
                if (cancelled) return;
                if (result.success) {
                    setUsers(result.users || []);
                    setCompanyInfo(result.company || null);
                    setTotalRecords(result.pagination?.total || 0);
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: result.message || 'Failed to load company users',
                        life: 5000,
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load company users:', err?.message || 'Unknown error');
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: err.message || 'Failed to load company users',
                        life: 5000,
                    });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadCompanyUsers();
        return () => { cancelled = true; };
    }, [first, rows, debouncedSearch, sortField, sortOrder, refreshNonce]);

    const refresh = () => setRefreshNonce((n) => n + 1);
    const handleClearSearch = () => setSearchQuery('');

    const onPage = (e) => {
        setFirst(e.first);
        setRows(e.rows);
    };

    const onSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };

    // Impersonation
    const handleImpersonateClick = useCallback((user) => {
        setConfirmDialog({ visible: true, user });
    }, []);

    const confirmImpersonation = async () => {
        const user = confirmDialog.user;
        if (!user) return;
        setImpersonating((prev) => ({ ...prev, [user.id]: true }));
        setConfirmDialog({ visible: false, user: null });

        try {
            const result = await companyImpersonationService.startImpersonation(user.id);
            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'User View Started',
                    detail: `Now viewing from ${user.name}'s perspective`,
                    life: 3000,
                });
                setTimeout(() => {
                    window.location.href = '/app/dashboard';
                }, 1000);
            }
        } catch (err) {
            console.error('Company impersonation failed:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'User View Failed',
                detail: err.message || 'Failed to start user view',
                life: 5000,
            });
        } finally {
            setImpersonating((prev) => ({ ...prev, [user.id]: false }));
        }
    };

    const cancelImpersonation = () => setConfirmDialog({ visible: false, user: null });

    // Edit
    const openEditDialog = useCallback((user) => {
        setEditingUser(user);
        setEditFormData({
            name: user.name || '',
            email: user.email || '',
            first_name: user.user_detail?.first_name || '',
            last_name: user.user_detail?.last_name || '',
            mobile_number: user.mobile_number || user.user_detail?.mobile_number || '',
            handled_lenders: user.handled_lenders || user.user_detail?.handled_lenders || [],
        });
        setFormErrors({});
        setEditDialog(true);
    }, []);

    const closeEditDialog = () => {
        setEditDialog(false);
        setEditingUser(null);
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};
        if (!editFormData.name.trim()) errors.name = 'Name is required';
        if (!editFormData.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
            errors.email = 'Please enter a valid email';
        }
        return errors;
    };

    const handleEditSubmit = async () => {
        if (!editingUser) return;
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({});
        setEditing(true);
        try {
            const response = await companyCreditService.updateEmployeeDetails(editingUser.id, editFormData);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Employee details updated successfully',
                    life: 3000,
                });
                closeEditDialog();
                refresh();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to update employee details',
                    life: 5000,
                });
            }
        } catch (err) {
            console.error('Failed to update employee details:', err?.message || 'Unknown error');
            const apiErrors = err.response?.data?.errors;
            if (apiErrors) {
                const fieldErrors = {};
                if (apiErrors.email) fieldErrors.email = 'This email is already in use by another user';
                if (apiErrors.mobile_number) fieldErrors.mobile_number = 'This mobile number is already in use by another user';
                if (Object.keys(fieldErrors).length > 0) {
                    setFormErrors(fieldErrors);
                    return;
                }
            }
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.response?.data?.message || 'Failed to update employee details',
                life: 5000,
            });
        } finally {
            setEditing(false);
        }
    };

    // Colleague sharing toggle
    const handleColleagueSharingToggle = useCallback(async (userId, newValue) => {
        setUpdatingSharing((prev) => ({ ...prev, [userId]: true }));
        try {
            const result = await leadSharingSettingsService.updateEmployeeSharingSettings(userId, newValue);
            if (result.success) {
                setUsers((prev) => prev.map((u) =>
                    u.id === userId ? { ...u, colleague_sharing_enabled: newValue } : u
                ));
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: result.message || `Colleague sharing ${newValue ? 'enabled' : 'disabled'}`,
                    life: 3000,
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to update sharing settings',
                    life: 5000,
                });
            }
        } catch (err) {
            console.error('Failed to update colleague sharing:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.response?.data?.message || 'Failed to update sharing settings',
                life: 5000,
            });
        } finally {
            setUpdatingSharing((prev) => ({ ...prev, [userId]: false }));
        }
    }, []);

    // Status toggle
    const handleStatusToggle = useCallback((user, newStatus) => {
        if (!newStatus) {
            setStatusConfirmDialog({ visible: true, user, newStatus });
        } else {
            confirmStatusChange(user, newStatus);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmStatusChange = async (user, newStatus) => {
        const targetUser = user || statusConfirmDialog.user;
        const targetStatus = newStatus !== undefined ? newStatus : statusConfirmDialog.newStatus;
        if (!targetUser) return;

        setConfirmingStatusChange(true);
        setUpdatingStatus((prev) => ({ ...prev, [targetUser.id]: true }));
        try {
            const response = await axiosInstance.patch(`/company/users/${targetUser.id}/status`, {
                is_active: targetStatus,
            });
            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: targetStatus ? 'Employee has been enabled' : 'Employee has been disabled',
                    life: 3000,
                });
                setUsers((prev) => prev.map((u) =>
                    u.id === targetUser.id ? { ...u, is_active: targetStatus } : u
                ));
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.message || 'Failed to update employee status',
                    life: 5000,
                });
            }
        } catch (err) {
            console.error('Failed to update employee status:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.response?.data?.message || 'Failed to update employee status',
                life: 5000,
            });
        } finally {
            setUpdatingStatus((prev) => ({ ...prev, [targetUser.id]: false }));
            setConfirmingStatusChange(false);
            setStatusConfirmDialog({ visible: false, user: null, newStatus: null });
        }
    };

    const cancelStatusChange = () => setStatusConfirmDialog({ visible: false, user: null, newStatus: null });

    // Column templates
    const nameBodyTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-medium text-sm text-900">{rowData.name}</span>
            {rowData.user_detail && (rowData.user_detail.first_name || rowData.user_detail.last_name) && (
                <small className="text-500">
                    {rowData.user_detail.first_name} {rowData.user_detail.last_name}
                </small>
            )}
        </div>
    );

    const emailBodyTemplate = (rowData) => (
        <span className="text-sm text-700">{rowData.email || '—'}</span>
    );

    const mobileBodyTemplate = (rowData) => {
        const mobile = rowData.mobile_number || rowData.user_detail?.mobile_number;
        return (
            <span className={mobile ? 'text-sm text-700' : 'text-sm text-500'}>
                {mobile || '—'}
            </span>
        );
    };

    // Handled Lenders — chips of lender names the employee handles. Prefer the
    // backend-resolved names; fall back to mapping stored IDs via lenderOptions.
    const handledLendersBodyTemplate = (rowData) => {
        let names = rowData.handled_lender_names;
        if (!names || names.length === 0) {
            const ids = rowData.handled_lenders || rowData.user_detail?.handled_lenders || [];
            names = ids
                .map((id) => lenderOptions.find((o) => o.value === id)?.label)
                .filter(Boolean);
        }
        if (!names || names.length === 0) return <span className="text-sm text-500">—</span>;
        return (
            <div className="flex flex-wrap gap-1">
                {names.map((n, i) => (
                    <Tag key={i} value={n} className="text-xs" />
                ))}
            </div>
        );
    };

    const lastLoginBodyTemplate = (rowData) => {
        if (!rowData.last_login_at) return <span className="text-sm text-500">Never</span>;
        const date = new Date(rowData.last_login_at);
        return (
            <div className="flex flex-column">
                <span className="text-sm text-700">
                    {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <small className="text-500">
                    {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </small>
            </div>
        );
    };

    const colleagueSharingTemplate = (rowData) => {
        const isUpdating = !!updatingSharing[rowData.id];
        const isEnabled = rowData.colleague_sharing_enabled ?? true;
        return (
            <div className="flex align-items-center gap-2">
                <InputSwitch
                    checked={isEnabled}
                    onChange={(e) => handleColleagueSharingToggle(rowData.id, e.value)}
                    disabled={isUpdating}
                />
                {isUpdating && <i className="pi pi-spin pi-spinner text-primary" aria-hidden="true" />}
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const isUpdating = !!updatingStatus[rowData.id];
        const isActive = rowData.is_active ?? true;
        return (
            <div className="flex align-items-center gap-2">
                <InputSwitch
                    checked={isActive}
                    onChange={(e) => handleStatusToggle(rowData, e.value)}
                    disabled={isUpdating}
                />
                {isUpdating ? (
                    <i className="pi pi-spin pi-spinner text-primary" aria-hidden="true" />
                ) : (
                    <Tag
                        value={isActive ? 'Active' : 'Disabled'}
                        severity={isActive ? 'success' : 'danger'}
                        className="text-xs"
                    />
                )}
            </div>
        );
    };

    const actionBodyTemplate = (rowData) => {
        const isCurrentlyImpersonating = !!impersonating[rowData.id];
        return (
            <div className="flex flex-wrap gap-1 justify-content-center">
                <Button
                    icon="pi pi-pencil"
                    severity="warning"
                    outlined
                    size="small"
                    aria-label={`Edit ${rowData.name}`}
                    tooltip="Edit employee details"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => openEditDialog(rowData)}
                />
                <Button
                    icon={isCurrentlyImpersonating ? 'pi pi-spin pi-spinner' : 'pi pi-eye'}
                    severity="info"
                    outlined
                    size="small"
                    aria-label={`View as ${rowData.name}`}
                    tooltip="View from this user's perspective"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleImpersonateClick(rowData)}
                    disabled={isCurrentlyImpersonating}
                    loading={isCurrentlyImpersonating}
                />
            </div>
        );
    };

    const confirmDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                severity="secondary"
                outlined
                size="small"
                onClick={cancelImpersonation}
            />
            <Button
                label="Start User View"
                icon="pi pi-eye"
                severity="info"
                size="small"
                onClick={confirmImpersonation}
                autoFocus
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header — stacks on mobile, side-by-side from md */}
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Users</h1>
                            <p className="text-600 text-sm m-0">
                                Manage employees and view applications from their perspective
                            </p>
                        </div>
                        <div className="flex flex-wrap align-items-center gap-2">
                            <Tag
                                value="Company Admin View"
                                severity="info"
                                icon="pi pi-shield"
                            />
                            <Tag
                                value={`Company: ${companyInfo?.name || 'Unknown'}`}
                                severity="secondary"
                                icon="pi pi-building"
                            />
                            <Button
                                label="Create Employee"
                                icon="pi pi-user-plus"
                                severity="success"
                                size="small"
                                onClick={() => setShowRegisterDialog(true)}
                            />
                        </div>
                    </div>

                    {/* Collapsible instructions — helpful, but out of the way by default */}
                    <Accordion className="mb-3">
                        <AccordionTab
                            header={
                                <span className="flex align-items-center gap-2 text-sm font-medium">
                                    <i className="pi pi-info-circle text-primary" />
                                    How to Use Company User Management
                                </span>
                            }
                        >
                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <i className="pi pi-user text-primary" />
                                        <span className="font-medium text-sm">Step 1: Select Employee</span>
                                    </div>
                                    <p className="text-600 text-sm m-0">
                                        Find the employee you want to view as in the table below and click &quot;View&quot;.
                                    </p>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <i className="pi pi-eye text-primary" />
                                        <span className="font-medium text-sm">Step 2: Employee Perspective</span>
                                    </div>
                                    <p className="text-600 text-sm m-0">
                                        You&apos;ll see the application exactly as that employee sees it, with their permissions and data.
                                    </p>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <i className="pi pi-arrow-left text-primary" />
                                        <span className="font-medium text-sm">Step 3: Switch Back</span>
                                    </div>
                                    <p className="text-600 text-sm m-0">
                                        Click &quot;Switch Back&quot; in the banner to return to your admin view.
                                    </p>
                                </div>
                            </div>
                        </AccordionTab>

                        <AccordionTab
                            header={
                                <span className="flex align-items-center gap-2 text-sm font-medium">
                                    <i className="pi pi-sliders-h text-primary" />
                                    Status &amp; Sharing Controls
                                </span>
                            }
                        >
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <Tag value="Active" severity="success" className="text-xs" />
                                        <span className="font-medium text-sm">Active status</span>
                                    </div>
                                    <p className="text-600 text-sm m-0 line-height-3">
                                        Employee can log in and use the application normally. All their
                                        credits, leads and permissions remain active.
                                    </p>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <Tag value="Disabled" severity="danger" className="text-xs" />
                                        <span className="font-medium text-sm">Disabled status</span>
                                    </div>
                                    <p className="text-600 text-sm m-0 line-height-3">
                                        Employee is logged out immediately and blocked from signing in
                                        until re-enabled. Existing data is preserved; disabling is
                                        reversible — toggle back to Active to restore access.
                                    </p>
                                </div>

                                <div className="col-12 md:col-6 mt-3">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <i className="pi pi-share-alt text-primary" />
                                        <span className="font-medium text-sm">Colleague Sharing — Enabled</span>
                                    </div>
                                    <p className="text-600 text-sm m-0 line-height-3">
                                        Employee can share their leads with colleagues in the same
                                        company. Useful for collaborative teams.
                                    </p>
                                </div>

                                <div className="col-12 md:col-6 mt-3">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <i className="pi pi-share-alt text-500" />
                                        <span className="font-medium text-sm">Colleague Sharing — Disabled</span>
                                    </div>
                                    <p className="text-600 text-sm m-0 line-height-3">
                                        Employee cannot share leads with colleagues. Their leads stay
                                        private to them. Safer for sensitive or quota-based setups.
                                    </p>
                                </div>

                                <div className="col-12 mt-3">
                                    <div className="surface-100 border-round p-3 flex align-items-start gap-2">
                                        <i className="pi pi-info-circle text-primary mt-1" aria-hidden="true" />
                                        <small className="text-600 line-height-3">
                                            Both toggles save <strong>instantly</strong> — there is no
                                            Save button. Disabling a status prompts for confirmation;
                                            enabling happens immediately.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>

                    <Card>
                        {/* Filter bar — 5-slot grid: 1 filter + 3 spacers + action block (P7 golden rule) */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search by name, email, mobile..."
                                            aria-label="Search employees"
                                            className="w-full p-inputtext-sm"
                                        />
                                    </IconField>
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        onClick={handleClearSearch}
                                        disabled={!searchQuery}
                                        aria-label="Clear search"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                    <Button
                                        icon="pi pi-refresh"
                                        label="Refresh"
                                        severity="info"
                                        outlined
                                        size="small"
                                        onClick={refresh}
                                        tooltip="Refresh employee list"
                                        tooltipOptions={{ position: 'top' }}
                                        aria-label="Refresh employee list"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={users}
                            lazy
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={onPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            loading={loading}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={onSort}
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-users pi-2x text-400 mb-3" />
                                    <span className="text-600">No company users found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="name"
                                header="Name"
                                body={nameBodyTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="email"
                                header="Email"
                                body={emailBodyTemplate}
                                sortable
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="mobile_number"
                                header="Mobile"
                                body={mobileBodyTemplate}
                                style={{ minWidth: '130px' }}
                            />
                            <Column
                                field="handled_lenders"
                                header="Lender Manager"
                                body={handledLendersBodyTemplate}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="last_login_at"
                                header="Last Login"
                                body={lastLoginBodyTemplate}
                                sortable
                                style={{ minWidth: '140px' }}
                            />
                            <Column
                                field="colleague_sharing_enabled"
                                header="Colleague Sharing"
                                body={colleagueSharingTemplate}
                                style={{ minWidth: '160px' }}
                            />
                            <Column
                                field="is_active"
                                header="Status"
                                body={statusBodyTemplate}
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionBodyTemplate}
                                exportable={false}
                                style={{ minWidth: '140px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* Confirm Impersonation Dialog */}
            <Dialog
                visible={confirmDialog.visible}
                onHide={cancelImpersonation}
                header="Confirm User View"
                footer={confirmDialogFooter}
                style={{ width: '90vw', maxWidth: '450px' }}
                modal
                draggable={false}
            >
                <div className="flex align-items-start gap-3">
                    <i className="pi pi-exclamation-triangle text-orange-500 pi-2x" aria-hidden="true" />
                    <div className="flex flex-column gap-2">
                        <span>
                            Are you sure you want to view the application as{' '}
                            <strong>{confirmDialog.user?.name}</strong>?
                        </span>
                        <small className="text-600">
                            You will see the application exactly as this employee sees it.
                        </small>
                    </div>
                </div>
            </Dialog>

            {/* Edit Employee Dialog */}
            <Dialog
                visible={editDialog}
                onHide={() => !editing && closeEditDialog()}
                header="Edit Employee Details"
                modal
                draggable={false}
                closable={!editing}
                style={{ width: '90vw', maxWidth: '600px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={closeEditDialog}
                            disabled={editing}
                        />
                        <Button
                            label="Save Changes"
                            icon="pi pi-check"
                            size="small"
                            onClick={handleEditSubmit}
                            loading={editing}
                            disabled={editing}
                        />
                    </div>
                }
            >
                <div className="grid formgrid">
                    <div className="field col-12">
                        <label htmlFor="edit-name" className="block font-medium text-sm mb-2">
                            Username <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="edit-name"
                            value={editFormData.name}
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter username"
                            disabled={editing}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': formErrors.name })}
                            aria-describedby="edit-name-help"
                            aria-invalid={!!formErrors.name}
                        />
                        {formErrors.name && (
                            <small id="edit-name-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" />
                                {formErrors.name}
                            </small>
                        )}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="edit-email" className="block font-medium text-sm mb-2">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="edit-email"
                            type="email"
                            inputMode="email"
                            autoCapitalize="none"
                            spellCheck={false}
                            value={editFormData.email}
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, email: e.target.value }))}
                            placeholder="Enter email"
                            disabled={editing}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': formErrors.email })}
                            aria-describedby="edit-email-help"
                            aria-invalid={!!formErrors.email}
                        />
                        {formErrors.email && (
                            <small id="edit-email-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" />
                                {formErrors.email}
                            </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="edit-first-name" className="block font-medium text-sm mb-2">
                            First Name
                        </label>
                        <InputText
                            id="edit-first-name"
                            value={editFormData.first_name}
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                            placeholder="Enter first name"
                            disabled={editing}
                            className="w-full p-inputtext-sm"
                        />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="edit-last-name" className="block font-medium text-sm mb-2">
                            Last Name
                        </label>
                        <InputText
                            id="edit-last-name"
                            value={editFormData.last_name}
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                            placeholder="Enter last name"
                            disabled={editing}
                            className="w-full p-inputtext-sm"
                        />
                    </div>

                    <div className="field col-12">
                        <label htmlFor="edit-mobile" className="block font-medium text-sm mb-2">
                            Mobile Number
                        </label>
                        <InputText
                            id="edit-mobile"
                            inputMode="tel"
                            value={editFormData.mobile_number}
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, mobile_number: e.target.value }))}
                            placeholder="Enter mobile number"
                            disabled={editing}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': formErrors.mobile_number })}
                            aria-describedby="edit-mobile-help"
                            aria-invalid={!!formErrors.mobile_number}
                        />
                        {formErrors.mobile_number && (
                            <small id="edit-mobile-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" />
                                {formErrors.mobile_number}
                            </small>
                        )}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="edit-handled-lenders" className="block font-medium text-sm mb-2">
                            Lender Manager
                        </label>
                        <MultiSelect
                            id="edit-handled-lenders"
                            value={editFormData.handled_lenders}
                            options={lenderOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, handled_lenders: e.value }))}
                            placeholder="Select lenders this employee handles"
                            display="chip"
                            filter
                            showClear
                            disabled={editing}
                            className="w-full p-inputtext-sm"
                            emptyMessage="No lenders found"
                        />
                    </div>
                </div>
            </Dialog>

            {/* Employee Registration Dialog */}
            <EmployeeRegisterByAdminDialog
                visible={showRegisterDialog}
                onHide={() => setShowRegisterDialog(false)}
                onSuccess={() => {
                    setShowRegisterDialog(false);
                    refresh();
                }}
                toast={toast}
            />

            {/* Disable User Confirmation Dialog */}
            <Dialog
                visible={statusConfirmDialog.visible}
                onHide={() => !confirmingStatusChange && cancelStatusChange()}
                header="Confirm Disable Employee"
                style={{ width: '90vw', maxWidth: '450px' }}
                modal
                draggable={false}
                closable={!confirmingStatusChange}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={cancelStatusChange}
                            disabled={confirmingStatusChange}
                        />
                        <Button
                            label={confirmingStatusChange ? 'Disabling...' : 'Disable Employee'}
                            icon={confirmingStatusChange ? 'pi pi-spin pi-spinner' : 'pi pi-ban'}
                            severity="danger"
                            size="small"
                            onClick={() => confirmStatusChange()}
                            loading={confirmingStatusChange}
                            disabled={confirmingStatusChange}
                            autoFocus
                        />
                    </div>
                }
            >
                <div className="flex align-items-start gap-3">
                    <i className="pi pi-exclamation-triangle text-red-500 pi-2x" aria-hidden="true" />
                    <div className="flex flex-column gap-2">
                        <p className="m-0">
                            Are you sure you want to disable{' '}
                            <strong>{statusConfirmDialog.user?.name}</strong>?
                        </p>
                        <small className="text-600">
                            This employee will be logged out immediately and won&apos;t be able to login until re-enabled.
                        </small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default CompanyUserManagement;
